var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
import { alpha, Autocomplete, Checkbox, Chip, TextField, ThemeProvider, } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { memo, useCallback, useMemo, } from 'react';
import { useController } from 'react-hook-form';
import HelperText from '@/components/Form/HelpersText';
export const chipStyles = {
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
    fontWeight: 500,
};
const autocompleteTheme = createTheme({
    components: {
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    width: '100%',
                },
                popupIndicator: {
                    transform: 'none',
                },
                inputRoot: {
                    padding: '3px',
                    borderRadius: '6px',
                },
                paper: {
                    overflow: 'hidden',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '16px',
                    padding: '7px',
                    backgroundColor: '#fff',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    'fontSize': '16px',
                    '&:not(.Mui-focused,.MuiInputLabel-shrink,.MuiFormLabel-filled,.MuiInputLabel-sizeSmall)': {
                        transform: 'translate(16px, 9px) scale(1)',
                    },
                    '&.Mui-focused, &.MuiFormLabel-filled, &.MuiInputLabel-shrink': {
                        fontWeight: 600,
                        backgroundColor: '#fff',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        transform: 'translate(16px, -9px) scale(0.75)',
                    },
                },
                sizeSmall: {
                    '&:not(.Mui-focused,.MuiFormLabel-filled)': {
                        transform: 'translate(16px, 9px) scale(1)',
                    },
                    '&.MuiInputLabel-shrink, &.MuiFormLabel-filled': {
                        fontWeight: 600,
                        backgroundColor: '#fff',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        transform: 'translate(16px, -9px) scale(0.75)',
                    },
                },
            },
        },
    },
});
function AutocompleteSelect(_a) {
    var { name, defaultValue, label, helperText = '', disabled, required, multiple, options = [], itemValue = 'id', itemLabel = 'name', size, isNumericValue, isClearable, renderOption, placeholder, pluralValues, icon, withoutText, id, grouped = false, groupBy = 'group', readOnly } = _a, props = __rest(_a, ["name", "defaultValue", "label", "helperText", "disabled", "required", "multiple", "options", "itemValue", "itemLabel", "size", "isNumericValue", "isClearable", "renderOption", "placeholder", "pluralValues", "icon", "withoutText", "id", "grouped", "groupBy", "readOnly"]);
    const getValue = useCallback((option) => option[itemValue], [itemValue]);
    const getLabel = useCallback((option) => option[itemLabel], [itemLabel]);
    const { field, fieldState: { error }, } = useController({
        name,
        defaultValue: (defaultValue === null || defaultValue === void 0 ? void 0 : defaultValue[itemValue]) || null,
    });
    const renderTagsDefault = useCallback((value, getTagProps) => {
        return value.map((option, index) => {
            return (_createElement(Chip, Object.assign({}, getTagProps({ index }), { key: getValue(option), label: getLabel(option), size: "medium", sx: chipStyles })));
        });
    }, [getValue, getLabel]);
    const renderOptionDefault = useCallback((props, option, { selected }) => {
        return (_createElement("li", Object.assign({}, props, { key: getValue(option) }),
            multiple && (_jsx(Checkbox, { checked: selected, sx: { mr: 2 } })),
            getLabel(option)));
    }, [getValue, getLabel, multiple]);
    const normalizedValue = useMemo(() => {
        if (field.value) {
            if (multiple) {
                return options.filter((option) => {
                    return field.value.find((item) => String(item) === getValue(option));
                });
            }
            return options.find((option) => {
                return getValue(option) === field.value;
            }) || null;
        }
        return multiple ? [] : null;
    }, [field.value, options, multiple, getValue]);
    const handleChange = useCallback((_, selected) => {
        var _a;
        let parsedValue = null;
        if (multiple && Array.isArray(selected) && selected.length) {
            if (isNumericValue) {
                parsedValue = selected.map((option) => Number(getValue(option)));
            }
            else {
                parsedValue = selected.map((option) => getValue(option));
            }
        }
        else if (isNumericValue) {
            parsedValue = selected && !Array.isArray(selected) ? Number(getValue(selected)) : null;
        }
        else {
            parsedValue = selected && !Array.isArray(selected) ? getValue(selected) : null;
        }
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, undefined, selected);
        field.onChange(parsedValue);
    }, [multiple, isNumericValue, props, field, getValue]);
    const getOptionLabel = useCallback((option) => {
        if (typeof option !== 'object') {
            const labelOption = options.find((o) => getValue(o) === String(option));
            return getLabel(labelOption || {});
        }
        const labelOption = options.find((o) => getValue(o) === getValue(option));
        return (labelOption ? getLabel(labelOption) : getLabel(option));
    }, [options, getValue, getLabel]);
    const isOptionEqualToValue = useCallback((option, value) => {
        return getValue(option) === getValue(value);
    }, [getValue]);
    const groupByFn = useMemo(() => {
        return grouped ? (option) => option[groupBy] : undefined;
    }, [grouped, groupBy]);
    const renderInput = useCallback((params) => {
        var _a;
        return (_jsx(TextField, Object.assign({}, params, { id: id, disabled: disabled, error: error !== undefined, helperText: !withoutText
                && _jsx(HelperText, { message: (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : helperText, pluralValues: pluralValues }), label: label, placeholder: placeholder, required: required, size: size, inputProps: Object.assign(Object.assign(Object.assign({}, params.inputProps), { readOnly }), (readOnly && { inputMode: 'none' })) })));
    }, [id, disabled, error, withoutText, helperText, pluralValues, label, placeholder, required, size, readOnly]);
    return (_jsx(ThemeProvider, { theme: autocompleteTheme, children: _jsx(Autocomplete, Object.assign({ options: options, defaultValue: defaultValue }, field, { disableClearable: !isClearable, disableCloseOnSelect: multiple, disabled: disabled, id: id, groupBy: groupByFn, getOptionLabel: getOptionLabel, isOptionEqualToValue: isOptionEqualToValue, multiple: multiple, onChange: handleChange, renderInput: renderInput, renderOption: renderOption || renderOptionDefault, renderTags: renderTagsDefault, size: size, value: normalizedValue, popupIcon: icon })) }));
}
export default memo(AutocompleteSelect);
